<!-- ══ Meeting Invitation Dock ══ -->
<section class="dock" [class.minimized]="isMinimized" [class.expanded]="isExpanded"
  [class.dragging-over]="isDraggingOver" (dragover)="onDragOver($event)" (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)">

  <!-- Drag & Drop Overlay -->
  <div class="drag-drop-overlay" *ngIf="isDraggingOver">
    <div class="drag-drop-box">
      <svg width="24" height="24" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"
        stroke-linecap="round">
        <path
          d="M14 8.5l-6 6a4 4 0 0 1-5.65-5.65l6.36-6.36a2.5 2.5 0 0 1 3.54 3.54L6 12.35a1 1 0 0 1-1.41-1.41L10.5 5" />
      </svg>
      Drop files here to attach
    </div>
  </div>

  <!-- ── Header ─────────────────────────────────────────────────────────── -->
  <div class="dock-header">
    <div class="dock-header-title">
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
        <rect x="1" y="3" width="14" height="12" rx="2" stroke="#fff" stroke-width="1.2" />
        <path d="M5 1v4M11 1v4M1 7h14" stroke="#fff" stroke-width="1.2" stroke-linecap="round" />
        <rect x="4" y="9" width="2.5" height="2.5" rx="0.5" fill="#e85d2f" />
        <rect x="6.75" y="9" width="2.5" height="2.5" rx="0.5" fill="rgba(255,255,255,0.35)" />
        <rect x="9.5" y="9" width="2.5" height="2.5" rx="0.5" fill="rgba(255,255,255,0.35)" />
      </svg>
      {{ isEditMode ? 'Edit Meeting Invitation' : 'New Meeting Invitation' }}
    </div>
    <div class="dock-header-actions">
      <button class="dock-icon-btn" type="button" (click)="toggleMinimize()"
        [matTooltip]="isMinimized ? 'Restore' : 'Minimise'">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
          <rect x="1" y="5.5" width="10" height="1.5" rx="0.75" />
        </svg>
      </button>
      <button class="dock-icon-btn" type="button" (click)="toggleExpand()"
        [matTooltip]="isExpanded ? 'Restore' : 'Full screen'">
        <svg *ngIf="!isExpanded" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
          stroke-width="1.3">
          <rect x="1.5" y="1.5" width="9" height="9" rx="1.5" />
          <path d="M4.5 1.5v3H1.5M7.5 10.5V7.5h3" />
        </svg>
        <svg *ngIf="isExpanded" width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor"
          stroke-width="1.3">
          <path d="M1.5 4.5H4.5V1.5M7.5 1.5V4.5H10.5M10.5 7.5H7.5V10.5M4.5 10.5V7.5H1.5" />
        </svg>
      </button>
      <button class="dock-icon-btn close" type="button" (click)="onClose()" matTooltip="Close">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"
          stroke-linecap="round">
          <path d="M2 2l8 8M10 2l-8 8" />
        </svg>
      </button>
    </div>
  </div>

  <!-- ── Body ───────────────────────────────────────────────────────────── -->
  <div class="dock-body">
    <form [formGroup]="eventForm" (ngSubmit)="onSubmit()" novalidate *ngIf="eventForm">

      <!-- Title -->
      <div class="field-row">
        <div class="field-label">Title</div>
        <div class="field-input-wrap">
          <input type="text" formControlName="title" placeholder="Meeting title" autocomplete="off" />
        </div>
      </div>
      <div class="field-error-row" *ngIf="isFieldInvalid('title')">
        {{ eventForm.get('title')?.hasError('required') ? 'Title is required' : 'Max 255 characters' }}
      </div>

      <!-- To / Attendees -->
      <div class="field-row attendee-row">
        <div class="field-label" style="padding-top: 11px">To</div>
        <div class="field-input-wrap attendees-wrap">

          <span class="attendee-tag" *ngFor="let attendee of attendees.controls; let i = index">
            <span class="av" [style.background]="getAvatarBg(i)">
              {{ getInitials(attendee.get('name')?.value || '') }}
            </span>
            {{ attendee.get('name')?.value }}
            <span class="tag-remove" (click)="removeAttendee(i)">×</span>
          </span>

          <input type="text" formControlName="attendeeInput" placeholder="Add attendee..."
            (input)="onAttendeeInputChange($any($event.target).value)"
            (keydown.enter)="addAttendeeFromInput(); $event.preventDefault()"
            (keydown.comma)="addAttendeeFromInput(); $event.preventDefault()" autocomplete="off"
            [matAutocomplete]="attendeeAuto" style="min-width: 110px; flex: 1;" />

          <mat-autocomplete #attendeeAuto="matAutocomplete" (optionSelected)="selectAttendee($event.option.value)">

            <mat-option *ngFor="let a of filteredAttendees$ | async" [value]="a">
              <div class="autocomplete-option">
                <span class="auto-email">{{ a.email }}</span>
              </div>
            </mat-option>

          </mat-autocomplete>
        </div>
      </div>
      <div class="field-error-row" *ngIf="isFieldInvalid('attendees')">
        At least one attendee is required.
      </div>

      <!-- When -->
      <div class="datetime-row">
        <div class="dt-label">When</div>
        <div class="dt-fields">

          <!-- Start date -->
          <div class="dt-date-wrap">
            <input [matDatepicker]="startPicker" formControlName="startDate" [min]="minDate" class="dt-date-input"
              readonly (click)="startPicker.open()" />
            <mat-datepicker #startPicker></mat-datepicker>
            <mat-datepicker-toggle [for]="startPicker" class="dt-cal-toggle"></mat-datepicker-toggle>
          </div>

          <span class="dt-sep">·</span>

          <!-- Start time -->
          <div class="dt-time-wrap dt-time-picker" *ngIf="!isAllDay" (click)="openStartTimePicker(); $event.stopPropagation()" [class.open]="showStartTimePicker">
            <span class="dt-time-display">{{ formatTimeDisplay(eventForm.get('startTime')?.value) }}</span>
            <div class="dt-time-dropdown" *ngIf="showStartTimePicker" (click)="$event.stopPropagation()">
              <ul>
                <li *ngFor="let slot of timeSlots"
                  [class.selected]="slot === formatTimeDisplay(eventForm.get('startTime')?.value)"
                  (click)="selectStartTime(convertDisplayTo24(slot))">
                  {{ slot }}
                </li>
              </ul>
            </div>
          </div>

          <span class="dt-sep arrow" *ngIf="!isAllDay">→</span>

          <!-- End time -->
          <div class="dt-time-wrap dt-time-picker" *ngIf="!isAllDay" (click)="openEndTimePicker(); $event.stopPropagation()" [class.open]="showEndTimePicker">
            <span class="dt-time-display">{{ formatTimeDisplay(eventForm.get('endTime')?.value) }}</span>
            <div class="dt-time-dropdown" *ngIf="showEndTimePicker" (click)="$event.stopPropagation()">
              <ul>
                <li *ngFor="let slot of endTimeSlots"
                  [class.selected]="slot.value === eventForm.get('endTime')?.value"
                  (click)="selectEndTime(slot.value)">
                  <span class="slot-time">{{ slot.label }}</span>
                  <span class="slot-duration">{{ slot.duration }}</span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Timezone badge -->
          <div class="tz-wrapper">
            <span class="tz-badge tz-badge--loading" *ngIf="isTimezoneLoading" title="Detecting timezone...">
              <mat-spinner diameter="14"></mat-spinner>
            </span>
            <span class="tz-badge" *ngIf="!isTimezoneLoading" (click)="openTzPicker()" [title]="selectedTz.label">
              {{ selectedTz.abbr }}
            </span>
            <!-- Timezone popup — anchored right so it doesn't overflow -->
            <div class="tz-popup" *ngIf="showTzPicker">
              <div class="tz-popup-inner">
                <div class="tz-search-wrap">
                  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#aaa" stroke-width="1.5"
                    stroke-linecap="round">
                    <circle cx="6.5" cy="6.5" r="4" />
                    <path d="M11 11l3 3" />
                  </svg>
                  <input type="text" placeholder="Search timezone..." [(ngModel)]="tzSearch"
                    [ngModelOptions]="{standalone: true}" (input)="filterTimezones(tzSearch)" autocomplete="off" />
                  <button type="button" class="tz-close-btn" (click)="closeTzPicker()">
                    <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.5"
                      stroke-linecap="round">
                      <path d="M2 2l8 8M10 2l-8 8" />
                    </svg>
                  </button>
                </div>
                <ul class="tz-list">
                  <li *ngFor="let tz of filteredTimezones" [class.selected]="tz.abbr === selectedTz.abbr && tz.label === selectedTz.label && tz.offset === selectedTz.offset && tz.iana === selectedTz.iana"
                    (click)="selectTz(tz)">
                    <span class="tz-name">{{ tz.label }} ({{ tz.abbr }})</span>
                    <span class="tz-offset">UTC {{ tz.offset }}</span>
                    <svg class="tz-check" viewBox="0 0 13 13" fill="none" stroke="#e85d2f" stroke-width="1.8"
                      stroke-linecap="round" stroke-linejoin="round">
                      <path d="M1.5 6.5l3.5 3.5 6-6" />
                    </svg>
                  </li>
                  <li *ngIf="filteredTimezones.length === 0" class="tz-no-results">No timezones found</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- All-day toggle -->
          <div class="allday-toggle" (click)="toggleAllDay()" title="All day">
            <div class="toggle-track" [class.on]="isAllDay">
              <div class="toggle-knob"></div>
            </div>
            <span class="toggle-label">All day</span>
          </div>
        </div>
      </div>
      <div class="field-error-row" *ngIf="isFieldInvalid('startDate')">
        Start date is required.
      </div>
      <div class="field-error-row" *ngIf="isFieldInvalid('startTime') && !isAllDay">
        Start time is required.
      </div>
      <div class="field-error-row" *ngIf="isFieldInvalid('endTime') && !isAllDay">
        End time is required.
      </div>
      <div class="field-error-row" *ngIf="eventForm.hasError('invalidDateRange') && eventForm.touched">
        End time must be after start time.
      </div>

      <!-- Repeat -->
      <div class="recur-row" formGroupName="recurrence">
        <div class="recur-label">Repeat</div>
        <div class="recur-content">
          <!-- Segmented control -->
          <div class="seg-control">
            <button type="button" class="seg-btn" [class.active]="recurrenceType === 'none'"
              (click)="setRecurrenceType('none')">None</button>
            <button type="button" class="seg-btn" [class.active]="recurrenceType === 'daily'"
              (click)="setRecurrenceType('daily')">Daily</button>
            <button type="button" class="seg-btn" [class.active]="recurrenceType === 'weekly'"
              (click)="setRecurrenceType('weekly')">Weekly</button>
            <button type="button" class="seg-btn" [class.active]="recurrenceType === 'monthly'"
              (click)="setRecurrenceType('monthly')">Monthly</button>
            <button type="button" class="seg-btn" [class.active]="recurrenceType === 'custom'"
              (click)="setRecurrenceType('custom')">Custom</button>
          </div>

          <!-- Custom recurrence options -->
          <div class="custom-recur" *ngIf="recurrenceType === 'custom'">
            <span class="cr-text">Every</span>
            <input type="number" formControlName="interval" class="cr-num cr-input" min="1" max="99" />
            <select class="cr-select" formControlName="pattern">
              <option value="daily">Day(s)</option>
              <option value="weekly">Week(s)</option>
              <option value="monthly">Month(s)</option>
            </select>
            <span class="cr-text">ends</span>
            <select class="cr-select" formControlName="endType">
              <option value="never">Never</option>
              <option value="on_date">On date</option>
              <option value="after_occurrences">After</option>
            </select>

            <!-- Material datepicker for end date -->
            <div class="cr-datepicker-wrap" *ngIf="recurrenceGroup.get('endType')?.value === 'on_date'">
              <input [matDatepicker]="recurEndPicker" formControlName="endDate" class="cr-date-input" readonly
                (click)="recurEndPicker.open()" placeholder="Pick date" />
              <mat-datepicker #recurEndPicker></mat-datepicker>
              <mat-datepicker-toggle [for]="recurEndPicker" class="cr-cal-toggle"></mat-datepicker-toggle>
            </div>

            <ng-container *ngIf="recurrenceGroup.get('endType')?.value === 'after_occurrences'">
              <input type="number" formControlName="occurrences" class="cr-num cr-input" min="1" />
              <span class="cr-text">occurrence(s)</span>
            </ng-container>
          </div>

          <!-- Weekly day picker -->
          <div class="week-days-row" *ngIf="recurrenceType === 'weekly'">
            <span class="cr-text">On:</span>
            <div class="day-chips">
              <button type="button" *ngFor="let d of weekDays; let i = index" class="day-chip"
                [class.selected]="isDaySelected(i)" (click)="toggleDay(i)">{{ d }}</button>
            </div>
          </div>
        </div>
      </div>

      <!--Meeting URL-->
      <div class="meeting-url-row">
        <div class="meeting-url-label">Meeting URL</div>
        <div class="field-input-wrap">
          <div class="meeting-url-input-wrap">
            <input type="text" formControlName="onlineMeetingUrl" placeholder="Meeting URL" />
          </div>
        </div>
      </div>
      <div class="field-error-row" *ngIf="isFieldInvalid('onlineMeetingUrl')">
        Meeting URL is required.
      </div>

      <!-- Attached Files Display -->
      <div class="attached-file-list" *ngIf="attachments.length > 0">
        <div class="attached-list" *ngFor="let file of attachments; let i = index">
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"
            stroke-linecap="round">
            <path
              d="M14 8.5l-6 6a4 4 0 0 1-5.65-5.65l6.36-6.36a2.5 2.5 0 0 1 3.54 3.54L6 12.35a1 1 0 0 1-1.41-1.41L10.5 5" />
          </svg>
          {{ file.name }}
          <span class="close" (click)="removeAttachment(i)">×</span>
        </div>
      </div>

      <!-- Description — same editor as MailContentComponent -->
      <div class="desc-row">
        <div class="NgxEditor__Wrapper">
          <div #descSummernote [ngxSummernote]="descConfig" [(summernoteModel)]="descriptionHTML"
            (summernoteModelChange)="onDescriptionChange($event)">
          </div>
        </div>
      </div>

    </form>
  </div>

  <!-- ── Toolbar ─────────────────────────────────────────────────────────── -->
  <div class="toolbar">
    <div class="toolbar-left">
      <button class="tb-btn" type="button" matTooltip="Attach file" (click)="fileInput.click()">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"
          stroke-linecap="round">
          <path
            d="M14 8.5l-6 6a4 4 0 0 1-5.65-5.65l6.36-6.36a2.5 2.5 0 0 1 3.54 3.54L6 12.35a1 1 0 0 1-1.41-1.41L10.5 5" />
        </svg>
      </button>
      <input type="file" #fileInput multiple (change)="onFileSelected($event)" hidden />
    </div>
    <div class="toolbar-right">
      <button class="tb-btn delete-btn" type="button" matTooltip="Discard" (click)="onClose()">
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.4"
          stroke-linecap="round">
          <path d="M3 4h10M6 4V2.5h4V4M5 4v9a1 1 0 0 0 1 1h4a1 1 0 0 0 1-1V4" />
        </svg>
      </button>
      <button class="send-btn" type="submit" (click)="onSubmit()" [disabled]="isSubmitting">
        <mat-spinner diameter="13" *ngIf="isSubmitting"></mat-spinner>
        <svg *ngIf="!isSubmitting" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="#fff"
          stroke-width="1.5" stroke-linecap="round">
          <path d="M14 2L2 6.5l5 1.5 1.5 5L14 2z" />
        </svg>
        {{ isSubmitting ? 'Sending...' : 'Send Invite' }}
      </button>
    </div>
  </div>

</section>